import React, { useCallback, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Card, CardBody, CardHeader, Col, Row } from "reactstrap";

import TableContainer from "Components/Common/TableContainer";

import { getSortObj } from "common/lib/tableDataOptions";
import { createTableSearchParams, parseTableFilters, parseTableSearch, parseTableSort } from "common/lib/tableQueryParams";
import * as moment from "moment";
// import ContractTypeFilter from "./ContractTypeFilter";
// import AgencyFilter from "./AgencyFilter";
import { normalize } from "common/lib/normalize";
import { toast } from "react-toastify";
// import Import from "./Import";
import { useProfile } from "Components/Hooks/UserHooks";
import { isAllowed } from "common/lib/permissionUtils";
import { getUserActivities } from "common/services/analytics.service";
import PageTypeFilter from "./PageTypeFilter";
import AgencyFilter from "pages/Contracts/AgencyFilter";
import {
  FilterDateRange,
  FilterLabel,
  FilterSearchBox,
} from "Components/Common/TableFilters";

interface DateRange {
  fromDate: Date | null;
  toDate: Date | null;
}

interface FilterValue {
  pageType: string;
  agency: string;
  dateRange: DateRange;
}

const AllLogs = ({
  isViewContractors,
  contractorId,
}: {
  isViewContractors?: boolean;
  isAgencyDashboard?: boolean;
  contractorId?: string;
}) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [params] = useState({
    page: Number(searchParams.get("page")) || 1, // Starts from 1
    pageSize: Number(searchParams.get("pageSize")) || 20, // Default page size
  });

  const [pagination, setPagination] = useState({
    pageIndex: params.page - 1, // Convert to zero-based index
    pageSize: params.pageSize,
  });

  // Update pageIndex
  const setPage = (newPage: number) => {
    setPagination((prevPagination) => ({
      ...prevPagination,
      pageIndex: newPage - 1,
    }));
  };

  const [sorted, setSorted] = useState(() =>
    parseTableSort(searchParams, [{ id: "createdAt", desc: true }])
  );

  const [searchValue, setSearchValue] = useState(parseTableSearch(searchParams));
  const defaultFilterValue: FilterValue = {
    pageType: "",
    agency: "",
    dateRange: {
      fromDate: null,
      toDate: null,
    },
  };
  const [filterValue, setFilterValue] = useState(() =>
    parseTableFilters(searchParams, defaultFilterValue)
  );

  const [apiParams, setApiParams] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [meta, setMeta] = useState<any>({});
  const onPageChange = useCallback(
    async ({
      page,
      sorted,
      searchValue,
      filterValue,
    }: {
      page: number;
      sorted: Array<{ id: string; desc: boolean }>;
      searchValue: string;
      filterValue?: any;
    }) => {
      const params = createTableSearchParams({
        page,
        pageSize: pagination.pageSize,
        sorted,
        searchValue,
        filterValue,
      });
      setSearchParams(params, { replace: true });

      const sortObj = getSortObj(sorted);

      let andConditions = [];

      if (filterValue?.agency) {
        andConditions.push({
          user: {
            agency: {
              id: {
                $eq: filterValue.agency,
              },
            },
          },
        });
      }
      if (filterValue?.pageType) {
        andConditions.push({
          frontendPage: {
            id: {
              $eq: filterValue.pageType,
            },
          },
        });
      }
      if (filterValue?.dateRange?.fromDate && filterValue?.dateRange?.toDate) {
        const fromDate = new Date(
          filterValue.dateRange.fromDate
        ).toLocaleDateString("en-CA");
        const toDate = new Date(
          filterValue.dateRange.toDate
        ).toLocaleDateString("en-CA");
        andConditions.push({
          createdAt: {
            $between: [fromDate, toDate],
          },
        });
      }

      andConditions.push({
        $or: [
          {
            user: {
              firstName: {
                $containsi: searchValue,
              },
            },
          },
          {
            contractor: {
              contractorName: {
                $containsi: searchValue,
              },
            },
          },
          {
            contract: {
              contractTitle: {
                $containsi: searchValue,
              },
            },
          },
          {
            riskAssessment: {
              reportTitle: {
                $containsi: searchValue,
              },
            },
          },
          {
            pvr: {
              title: {
                $containsi: searchValue,
              },
            },
          },
          {
            capacityAssessment: {
              reportTitle: {
                $containsi: searchValue,
              },
            },
          },
          {
            person: {
              firstName: {
                $containsi: searchValue,
              },
            },
          },
        ],
      });

      try {
        setLoading(true);

        const apiParams = {
          pagination: {
            page,
            pageSize: pagination.pageSize,
          },
          sort: sortObj,
          populate: [
            "frontendPage",
            "cotnract",
            "contractor",
            "person",
            "capacityAssessment",
            "riskAssessment",
            "pvr",
            "contract",
            "worldBankBannedList",
            "usSanctionsList",
            "ukSanctionsList",
            "unDesignatedList",
            "ungmList",
            "euSanctionsList",
            "user",
            "user.agency",
          ],
          filters: {
            $and: andConditions,
          },
        };
        setApiParams(apiParams);
        const response: any = await getUserActivities(apiParams);
        setData(normalize(response.data));
        setMeta(response?.meta);
      } catch (e: any) {
        toast.error("Error while fetching data", {});
      } finally {
        setLoading(false);
      }
    },
    [pagination.pageSize, setSearchParams]
  );

  const { userProfile } = useProfile();
  const [columnVisibility] = useState({
    checkBoxColumn: isAllowed(userProfile?.permissions, [
      {
        resource: "user-activity",
        action: "delete",
      },
    ]),
  });

  const columns = useMemo(
    () => [
      {
        header: "User Name",
        accessorKey: "user.firstName",
        enableColumnFilter: false,
        id: "user.firstName",
        cell: (cell: any) => {
          return (
            `${cell.getValue()} ${cell.row.original?.user?.lastName}` ||
            "Not Provided"
          );
        },
      },
      {
        header: "Agency ",
        accessorKey: "user.agency.name",
        enableColumnFilter: false,
        id: "user.agency.name",
        cell: (cell: any) => {
          return `${cell.getValue()} ` || "Not Provided";
        },
      },
      {
        header: "Page Name",
        accessorKey: "frontendPage.name",
        id: "frontendPage.name",
        enableColumnFilter: false,
        cell: (cell: any) => {
          return <>{cell.getValue() || "Not Provided"}</>;
        },
      },
      {
        header: "Page Content",
        accessorKey: "pageContent",
        id: "pageContent",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => {
          const rowData = cell.row.original;

          return (
            <div style={{ whiteSpace: "pre-wrap", width: "300px" }}>
              <Link
                to={`${rowData?.pageUrl}`}
                className="fw-medium link-primary"
              >
                {rowData?.contractor && rowData?.contractor?.contractorName
                  ? rowData?.contractor?.contractorName
                  : rowData?.person && rowData?.person?.name
                  ? rowData?.person?.name
                  : rowData?.contract && rowData?.contract?.contractTitle
                  ? rowData?.contract?.contractTitle
                  : rowData?.lta && rowData?.lta?.contractTitle
                  ? rowData?.lta?.contractTitle
                  : rowData?.usSanctionsList && rowData?.usSanctionsList?.name
                  ? rowData?.usSanctionsList?.name
                  : rowData?.euSanctionsList && rowData?.euSanctionsList?.name
                  ? rowData?.euSanctionsList?.name
                  : rowData?.ungmList && rowData?.ungmList?.name
                  ? rowData?.ungmList?.name
                  : rowData?.unDesignatedList && rowData?.unDesignatedList?.name
                  ? rowData?.unDesignatedList?.name
                  : rowData?.worldBankBannedList &&
                    rowData?.worldBankBannedList?.name
                  ? rowData?.worldBankBannedList?.name
                  : rowData?.ukSanctionsList && rowData?.ukSanctionsList?.name
                  ? rowData?.ukSanctionsList?.name
                  : rowData?.unDesignatedList && rowData?.unDesignatedList?.name
                  ? rowData?.unDesignatedList?.name
                  : `${rowData?.pageUrl}`}
              </Link>
            </div>
          );
        },
      },

      {
        header: "IP Address",
        accessorKey: "ipAddress",
        enableColumnFilter: false,
        id: "ipAddress",
        cell: (cell: any) => {
          return cell.getValue() || "Not Provided";
        },
      },

      {
        header: "Created At",
        accessorKey: "createdAt",
        id: "createdAt",
        enableColumnFilter: false,
        cell: (cell: any) => {
          if (cell.getValue() === null) return null;
          const date = moment(new Date(cell?.getValue())).format("DD MMM Y");
          return <>{date}</>;
        },
      },
    ],
    []
  );

  return (
    <React.Fragment>
      <Card>
        <CardBody>
          <Row className="g-3 align-items-end">
            <Col xxl={4} lg={6}>
              <FilterSearchBox
                id="activity-log-search"
                placeholder="Search activity logs..."
                value={searchValue}
                onChange={(value: string) => {
                  setSearchValue(value);
                  setPage(1);
                }}
              />
            </Col>
            <Col xxl={2} lg={6}>
              <FilterLabel htmlFor="pageTypeFilter">Page Type</FilterLabel>
              <PageTypeFilter
                onChange={(value) => {
                  setFilterValue({ ...filterValue, pageType: value });
                  setPage(1);
                }}
                value={filterValue.pageType}
              />
            </Col>
            <Col xxl={3} lg={6}>
              <FilterDateRange
                id="activity-log-date-range"
                value={filterValue?.dateRange}
                onChange={(dateRange: any) => {
                  setFilterValue({ ...filterValue, dateRange });
                  setPage(1);
                }}
              />
            </Col>
            <Col xxl={3} lg={6}>
              <FilterLabel htmlFor="agencyFilter">Agency</FilterLabel>
              <AgencyFilter
                onChange={(value) => {
                  setFilterValue({ ...filterValue, agency: value });
                  setPage(1);
                }}
                value={filterValue.agency}
              />
            </Col>
          </Row>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <Row className="align-items-center g-3">
            <Col md={8}>
              <h5 className="card-title mb-1">Activity Logs</h5>
              <p className="text-muted mb-0">
                A detailed list of all activity logs for the CIMS users.
              </p>
            </Col>
            <div className="col-md-auto ms-auto">
              <div className="d-flex gap-2"></div>
            </div>
          </Row>
        </CardHeader>
        <CardBody>
          <TableContainer
            loading={loading}
            columns={columns}
            data={data || []}
            pagination={pagination}
            setPagination={setPagination}
            sorted={sorted}
            setSorted={setSorted}
            onPageChange={onPageChange}
            FilterSection={() => {}}
            totalRows={meta?.pagination?.total || 0}
            totalPages={meta?.pagination?.pageCount || 0}
            searchValue={searchValue}
            filterValue={filterValue}
            columnVisibility={columnVisibility}
            divClass="table-responsive table-card mb-4"
            tableClass="align-middle table-nowrap mb-0 table-striped"
            theadClass="table-light table-nowrap"
          />
        </CardBody>
      </Card>
    </React.Fragment>
  );
};

export default AllLogs;
